import React, { useState } from 'react';
import { Box, Button, Typography, Input } from '@mui/material';
import { useImageUpload } from '../hooks/useImageUpload';
import { AddPhotoAlternate, Delete, Upload, Close } from '@mui/icons-material';

export function ImageUpload({ onUpload }) {
  const {
    previewUrl,
    fileName,
    fileInputRef,
    handleThumbnailClick,
    handleFileChange,
    handleRemove,
  } = useImageUpload({
    onUpload,
  });

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = {
        target: {
          files: [file],
        },
      };
      handleFileChange(fakeEvent);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '400px',
        mx: 'auto',
        mb: 6,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
        boxShadow: 1,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Image Upload</Typography>
        <Typography variant="body2" color="text.secondary">
          Supported formats: JPG, PNG, GIF
        </Typography>
      </Box>

      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />

      {!previewUrl ? (
        <Box
          onClick={handleThumbnailClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            aspectRatio: '1/1',
            maxWidth: '200px',
            mx: 'auto',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'divider',
            bgcolor: isDragging ? 'primary.light' : 'action.hover',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'action.selected',
            },
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              boxShadow: 1,
            }}
          >
            <AddPhotoAlternate 
              sx={{ 
                color: 'primary.main',
                fontSize: 24
              }} 
            />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight="medium" color="primary">
              Click to select
            </Typography>
            <Typography variant="caption" color="text.secondary">
              or drag and drop file here
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', maxWidth: '200px', mx: 'auto' }}>
          <Box
            sx={{
              position: 'relative',
              aspectRatio: '1/1',
              overflow: 'hidden',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&:hover .overlay': {
                opacity: 1,
              },
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s',
              }}
            />
            <Box
              className="overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(0, 0, 0, 0.4)',
                opacity: 0,
                transition: 'opacity 0.3s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={handleThumbnailClick}
                sx={{ minWidth: 0, p: 0.75 }}
              >
                <Upload sx={{ fontSize: 16 }} />
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleRemove}
                sx={{ minWidth: 0, p: 0.75 }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
          {fileName && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {fileName}
              </Typography>
              <Button
                size="small"
                onClick={handleRemove}
                sx={{ 
                  minWidth: 0, 
                  p: 0.5,
                  color: 'primary.main'
                }}
              >
                <Close fontSize="small" />
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
} 